import React, { useState } from 'react';
import { Mail, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

export const EmailVerificationScreen: React.FC = () => {
  const { firebaseUser, resendVerification, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleResendVerification = async () => {
    try {
      setResending(true);
      await resendVerification();
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      setChecking(true);
      
      // Reload the Firebase user to get updated emailVerified status
      await firebaseUser?.reload();
      
      // Check if email is now verified
      if (firebaseUser?.emailVerified) {
        // Email is verified! Reload the page to trigger the auth flow
        window.location.reload();
      } else {
        // Email not verified yet
        alert('Please click the verification link in your email first, then come back and click this button again.');
      }
    } catch (error) {
      console.error('Failed to check verification status:', error);
      alert('Failed to check verification. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Mail className="mx-auto h-24 w-24 text-primary mb-6" />
          <h2 className="text-3xl font-extrabold text-foreground mb-2">
            Verify Your Email
          </h2>
          <p className="text-sm text-muted-foreground">
            We've sent a verification email to:
          </p>
          <p className="text-lg font-medium text-foreground mt-2">
            {firebaseUser?.email}
          </p>
        </div>

        <div className="bg-card shadow rounded-lg p-6 space-y-4 border">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">Next Steps</h3>
                <div className="mt-1 text-sm text-blue-700">
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Check your email inbox <strong>and spam/junk folder</strong></li>
                    <li>Click the verification link in the email</li>
                    <li>You'll see "Your email has been verified"</li>
                    <li><strong>Return to this tab</strong></li>
                    <li>Click "I've verified my email" below</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {resent && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                <p className="text-sm text-green-700">
                  Verification email sent successfully! Check your inbox.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleCheckVerification}
              disabled={checking}
              className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 font-medium transition-colors"
            >
              {checking ? (
                <>
                  <RefreshCw className="animate-spin h-4 w-4" />
                  Checking verification...
                </>
              ) : (
                "I've verified my email"
              )}
            </button>

            <button
              onClick={handleResendVerification}
              disabled={resending}
              className="w-full bg-secondary text-secondary-foreground py-2 px-4 rounded-lg hover:bg-secondary/80 disabled:opacity-50 flex items-center justify-center gap-2 border transition-colors"
            >
              {resending ? (
                <>
                  <RefreshCw className="animate-spin h-4 w-4" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Resend verification email
                </>
              )}
            </button>

            <div className="text-center pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">
                Wrong email address?
              </p>
              <button
                onClick={handleLogout}
                className="text-sm text-red-600 hover:text-red-800 underline transition-colors"
              >
                Sign out and register with a different email
              </button>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Email verification is required to prevent unauthorized access. 
                Once verified, you'll have access to set up your profile.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};